import type {
  CreateWorkflow,
  PublishWorkflow,
  Workflow,
  WorkflowDetail,
  WorkflowDraft,
  WorkflowVersion,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';

const workflowsPath = (projectId: string) => adminPath(`/projects/${projectId}/workflows`);

/** `GET /admin/v1/projects/{id}/workflows` (array; `publishedVersion`, `draftDirty`). */
export function useWorkflows(projectId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').workflows,
    queryFn: ({ signal }) => request<Workflow[]>('GET', workflowsPath(projectId ?? ''), { signal }),
    enabled: projectId !== undefined,
  });
}

/** `GET .../workflows/{wid}`: the workflow with its editable `draft`. */
export function useWorkflow(projectId: string | undefined, workflowId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').workflow(workflowId ?? ''),
    queryFn: ({ signal }) =>
      request<WorkflowDetail>('GET', `${workflowsPath(projectId ?? '')}/${workflowId}`, { signal }),
    enabled: projectId !== undefined && workflowId !== undefined,
  });
}

/** `GET .../workflows/{wid}/versions` (array, newest first). */
export function useWorkflowVersions(projectId: string | undefined, workflowId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').workflowVersions(workflowId ?? ''),
    queryFn: ({ signal }) =>
      request<WorkflowVersion[]>(
        'GET',
        `${workflowsPath(projectId ?? '')}/${workflowId}/versions`,
        {
          signal,
        },
      ),
    enabled: projectId !== undefined && workflowId !== undefined,
  });
}

export function useWorkflowVersion(
  projectId: string | undefined,
  workflowId: string | undefined,
  version: number | undefined,
) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').workflowVersion(workflowId ?? '', version ?? 0),
    queryFn: ({ signal }) =>
      request<WorkflowVersion>(
        'GET',
        `${workflowsPath(projectId ?? '')}/${workflowId}/versions/${version}`,
        { signal },
      ),
    enabled: projectId !== undefined && workflowId !== undefined && version !== undefined,
  });
}

export function useCreateWorkflow(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateWorkflow) =>
      request<WorkflowDetail>('POST', workflowsPath(projectId), { body: input }),
    onSuccess: () => invalidate(keys.project(projectId).workflows, keys.projects.detail(projectId)),
  });
}

/** `PUT .../workflows/{wid}/draft`: replaces the draft; 400 `errors[]` by path on rule violations. */
export function useSaveWorkflowDraft(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: WorkflowDraft }) =>
      request<WorkflowDetail>('PUT', `${workflowsPath(projectId)}/${id}/draft`, { body: draft }),
    onSuccess: (_wf, { id }) =>
      invalidate(keys.project(projectId).workflow(id), keys.project(projectId).workflows),
  });
}

/**
 * `POST .../workflows/{wid}/publish`. 409 `workflow.status_mapping_required` lists the removed
 * statuses that still have tickets; re-submit with `statusMapping`.
 */
export function usePublishWorkflow(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<PublishWorkflow> & { id: string }) =>
      request<WorkflowDetail>('POST', `${workflowsPath(projectId)}/${id}/publish`, {
        body: { statusMapping: input.statusMapping ?? {} },
      }),
    onSuccess: (_wf, { id }) =>
      invalidate(
        keys.project(projectId).workflow(id),
        keys.project(projectId).workflows,
        keys.project(projectId).workflowVersions(id),
        keys.projects.detail(projectId),
        keys.jobs.all,
      ),
  });
}

/** `POST .../workflows/{wid}/versions/{v}/revert`: copies a published version into the draft. */
export function useRevertWorkflowVersion(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      request<WorkflowDetail>(
        'POST',
        `${workflowsPath(projectId)}/${id}/versions/${version}/revert`,
        {
          body: {},
        },
      ),
    onSuccess: (_wf, { id }) =>
      invalidate(keys.project(projectId).workflow(id), keys.project(projectId).workflows),
  });
}
